import React, {
    useEffect,
    useMemo,
    useState,
} from "react";

import api from "../api/api";

import ParentsStudentGrades from "../pages/Students/ParentsStudentGrades";

import defaultAvatar from "./maleee.jpg";

import TransActionPerStudent from "../SchoolDashApp/DailyTransaction/TransActionPerStudent";

import {
    Avatar,
    Box,
    Button,
    Chip,
    CircularProgress,
    Divider,
    IconButton,
    InputAdornment,
    Paper,
    Stack,
    TextField,
    Typography,
} from "@mui/material";

import {
    Search,
    ArrowBack,
    ArrowForward,
    ArrowBackIosNew,
    School,
    AccountBalanceWallet,
    EventAvailable,
    ReportProblem,
    Person,
    Groups,
    Payments,
    MenuBook,
    AccessTime,
    CheckCircle,
    Cancel,
    Warning,
    Refresh,
} from "@mui/icons-material";


const COLORS = {
    primary: "#e87519",
    primaryDark: "#bd4e00",
    primarySoft: "#fff4e8",

    black: "#111827",
    dark: "#1f2937",

    white: "#ffffff",

    text: "#172033",
    secondary: "#64748b",
    muted: "#94a3b8",

    background: "#f7f9fc",
    surface: "#ffffff",
    surfaceSoft: "#fffaf5",

    border: "#e5eaf1",
    borderStrong: "#d8dee8",

    green: "#15803d",
    greenSoft: "#ecfdf3",

    red: "#dc2626",
    redSoft: "#fef2f2",

    blue: "#2563eb",
    blueSoft: "#eff6ff",

    amber: "#d97706",
    amberSoft: "#fffbeb",

    purple: "#7c3aed",
    purpleSoft: "#f5f3ff",
};


const formatCurrency = (value) => {
    const number = parseFloat(value);

    if (Number.isNaN(number)) {
        return "$0.00";
    }

    return `$${number.toFixed(2)}`;
};


const InfoRow = ({
    label,
    value,
    icon,
}) => (
    <Box
        sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 2,
            py: 1.25,
            borderBottom: `1px solid ${COLORS.border}`,

            "&:last-child": {
                borderBottom: "none",
            },
        }}
    >
        <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{
                minWidth: 0,
            }}
        >
            {icon && (
                <Box
                    sx={{
                        width: 30,
                        height: 30,
                        borderRadius: "9px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: COLORS.primarySoft,
                        color: COLORS.primaryDark,
                        flexShrink: 0,
                    }}
                >
                    {icon}
                </Box>
            )}

            <Typography
                sx={{
                    color: COLORS.secondary,
                    fontSize: {
                        xs: "0.78rem",
                        sm: "0.84rem",
                    },
                    fontWeight: 700,
                }}
            >
                {label}
            </Typography>
        </Stack>

        <Typography
            sx={{
                color: COLORS.text,
                fontSize: {
                    xs: "0.8rem",
                    sm: "0.88rem",
                },
                fontWeight: 800,
                textAlign: "right",
                wordBreak: "break-word",
                maxWidth: "58%",
            }}
        >
            {value || "N/A"}
        </Typography>
    </Box>
);


const SectionCard = ({
    title,
    subtitle,
    icon,
    children,
    fullWidth = false,
}) => (
    <Paper
        elevation={0}
        sx={{
            flex: fullWidth
                ? "1 1 100%"
                : "1 1 320px",

            minWidth: {
                xs: "100%",
                md: 300,
            },

            p: {
                xs: 1.8,
                sm: 2.25,
                md: 2.5,
            },

            borderRadius: {
                xs: "16px",
                md: "20px",
            },

            background: COLORS.surface,

            border: `1px solid ${COLORS.border}`,

            boxShadow:
                "0 8px 28px rgba(15,23,42,0.045)",

            display: "flex",
            flexDirection: "column",
            gap: 2,

            transition:
                "box-shadow .2s ease, transform .2s ease",

            "&:hover": {
                boxShadow:
                    "0 12px 32px rgba(15,23,42,0.07)",
            },
        }}
    >
        <Stack
            direction="row"
            spacing={1.25}
            alignItems="center"
        >
            <Box
                sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "11px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: COLORS.primarySoft,
                    color: COLORS.primaryDark,
                    flexShrink: 0,
                }}
            >
                {icon}
            </Box>

            <Box
                sx={{
                    minWidth: 0,
                }}
            >
                <Typography
                    sx={{
                        fontWeight: 850,
                        color: COLORS.text,
                        fontSize: {
                            xs: "0.95rem",
                            sm: "1rem",
                        },
                    }}
                >
                    {title}
                </Typography>

                {subtitle && (
                    <Typography
                        sx={{
                            mt: 0.25,
                            color: COLORS.secondary,
                            fontSize: {
                                xs: "0.72rem",
                                sm: "0.78rem",
                            },
                        }}
                    >
                        {subtitle}
                    </Typography>
                )}
            </Box>
        </Stack>

        {children}
    </Paper>
);


const StatCard = ({
    label,
    value,
    icon,
    color,
    background,
}) => (
    <Box
        sx={{
            p: {
                xs: 1.35,
                sm: 1.6,
            },

            minHeight: {
                xs: 92,
                sm: 100,
            },

            borderRadius: "14px",

            background:
                background || COLORS.surfaceSoft,

            border:
                `1px solid ${COLORS.border}`,

            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",

            transition:
                "all .2s ease",

            "&:hover": {
                transform:
                    "translateY(-2px)",

                boxShadow:
                    "0 8px 22px rgba(15,23,42,0.06)",
            },
        }}
    >
        <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={1}
        >
            <Typography
                sx={{
                    color: COLORS.secondary,
                    fontSize: {
                        xs: "0.7rem",
                        sm: "0.76rem",
                    },
                    fontWeight: 750,
                }}
            >
                {label}
            </Typography>

            <Box
                sx={{
                    width: 28,
                    height: 28,
                    borderRadius: "8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color,
                    background:
                        background || COLORS.primarySoft,
                    flexShrink: 0,
                }}
            >
                {icon}
            </Box>
        </Stack>

        <Typography
            sx={{
                mt: 1,
                color: color || COLORS.text,
                fontSize: {
                    xs: "1rem",
                    sm: "1.12rem",
                },
                fontWeight: 900,
                lineHeight: 1.2,
                wordBreak: "break-word",
            }}
        >
            {value}
        </Typography>
    </Box>
);


const LinkStudents = () => {
    const [children, setChildren] = useState([]);

    const [selectedChild, setSelectedChild] =
        useState(null);

    const [showGrades, setShowGrades] =
        useState(false);

    const [showPayments, setShowPayments] =
        useState(false);

    const [searchTerm, setSearchTerm] =
        useState("");

    const [complaintIndices, setComplaintIndices] =
        useState({});

    const [loading, setLoading] =
        useState(true);

    const [error, setError] =
        useState("");

    const token =
        localStorage.getItem("access");


    const fetchLinkedStudents = async () => {
        if (!token) {
            setLoading(false);
            setError(
                "Your session has expired. Please log in again."
            );
            return;
        }

        try {
            setLoading(true);
            setError("");

            const response = await api.get(
                "/admissions/my-linked-students/",
                {
                    headers: {
                        Authorization:
                            `Bearer ${token}`,
                    },
                }
            );

            const students =
                Array.isArray(response.data)
                    ? response.data
                    : [];

            setChildren(students);
        } catch (requestError) {
            console.error(
                "Error fetching linked students:",
                requestError
            );

            setError(
                "Unable to load your linked students. Please try again."
            );
        } finally {
            setLoading(false);
        }
    };


    useEffect(() => {
        fetchLinkedStudents();
    }, [token]);


    const filteredChildren = useMemo(() => {
        const query =
            searchTerm
                .trim()
                .toLowerCase();

        if (!query) {
            return children;
        }

        return children.filter((child) => {
            const fullName =
                child.full_name
                    ?.toLowerCase() || "";

            const grade =
                child.grade_class_name
                    ?.toLowerCase() || "";

            const division =
                child.division_assigned_name
                    ?.toLowerCase() || "";

            return (
                fullName.includes(query) ||
                grade.includes(query) ||
                division.includes(query)
            );
        });
    }, [
        children,
        searchTerm,
    ]);


    const selectChild = (child) => {
        setSelectedChild(child);
        setShowGrades(false);
        setShowPayments(false);

        window.scrollTo({
            top: 0,
            behavior: "smooth",
        });
    };


    const handleBackToChildren = () => {
        setSelectedChild(null);
        setShowGrades(false);
        setShowPayments(false);
    };


    const setChildComplaintIndex = (
        childId,
        newIndex
    ) => {
        setComplaintIndices((previous) => ({
            ...previous,
            [childId]: newIndex,
        }));
    };


    const currentComplaintIndex = (
        child
    ) =>
        complaintIndices[child.id] ?? 0;


    const handlePrev = (child) => {
        const index =
            currentComplaintIndex(child);

        if (index > 0) {
            setChildComplaintIndex(
                child.id,
                index - 1
            );
        }
    };


    const handleNext = (
        child,
        total
    ) => {
        const index =
            currentComplaintIndex(child);

        if (index < total - 1) {
            setChildComplaintIndex(
                child.id,
                index + 1
            );
        }
    };


    if (loading) {
        return (
            <Box
                sx={{
                    width: "100%",
                    py: {
                        xs: 7,
                        sm: 9,
                    },
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                }}
            >
                <CircularProgress
                    size={34}
                    thickness={4}
                    sx={{
                        color: COLORS.primary,
                    }}
                />

                <Typography
                    sx={{
                        mt: 2,
                        color: COLORS.text,
                        fontWeight: 800,
                        fontSize: "0.95rem",
                    }}
                >
                    Loading students...
                </Typography>

                <Typography
                    sx={{
                        mt: 0.5,
                        color: COLORS.secondary,
                        fontSize: "0.8rem",
                    }}
                >
                    Fetching students linked to your profile.
                </Typography>
            </Box>
        );
    }


    if (error) {
        return (
            <Paper
                elevation={0}
                sx={{
                    p: {
                        xs: 3,
                        sm: 4,
                    },
                    textAlign: "center",
                    borderRadius: "18px",
                    border:
                        `1px solid ${COLORS.border}`,
                    background: COLORS.surface,
                }}
            >
                <Box
                    sx={{
                        width: 54,
                        height: 54,
                        mx: "auto",
                        borderRadius: "15px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: COLORS.redSoft,
                        color: COLORS.red,
                    }}
                >
                    <Warning />
                </Box>

                <Typography
                    sx={{
                        mt: 1.75,
                        fontWeight: 850,
                        color: COLORS.text,
                        fontSize: "1rem",
                    }}
                >
                    Unable to load students
                </Typography>

                <Typography
                    sx={{
                        mt: 0.6,
                        color: COLORS.secondary,
                        fontSize: "0.82rem",
                        maxWidth: 420,
                        mx: "auto",
                    }}
                >
                    {error}
                </Typography>

                <Button
                    variant="contained"
                    startIcon={<Refresh />}
                    onClick={fetchLinkedStudents}
                    sx={{
                        mt: 2.5,
                        borderRadius: "10px",
                        px: 2.5,
                        textTransform: "none",
                        fontWeight: 800,
                        background:
                            COLORS.primary,
                        boxShadow: "none",
                        "&:hover": {
                            background:
                                COLORS.primaryDark,
                            boxShadow: "none",
                        },
                    }}
                >
                    Try Again
                </Button>
            </Paper>
        );
    }


    return (
        <Box
            sx={{
                width: "100%",
                minHeight: "100%",
                px: {
                    xs: 0,
                    sm: 0.5,
                    md: 1,
                },
                py: {
                    xs: 0.5,
                    sm: 1,
                },
            }}
        >
            {!selectedChild ? (
                <>
                    <Box
                        sx={{
                            mb: {
                                xs: 2,
                                sm: 2.5,
                            },
                        }}
                    >
                        <Stack
                            direction={{
                                xs: "column",
                                sm: "row",
                            }}
                            alignItems={{
                                xs: "flex-start",
                                sm: "center",
                            }}
                            justifyContent="space-between"
                            spacing={2}
                        >
                            <Stack
                                direction="row"
                                spacing={1.25}
                                alignItems="center"
                            >
                                <Box
                                    sx={{
                                        width: 44,
                                        height: 44,
                                        borderRadius:
                                            "12px",
                                        display:
                                            "flex",
                                        alignItems:
                                            "center",
                                        justifyContent:
                                            "center",
                                        background:
                                            COLORS.primarySoft,
                                        color:
                                            COLORS.primaryDark,
                                    }}
                                >
                                    <Groups />
                                </Box>

                                <Box>
                                    <Typography
                                        sx={{
                                            fontWeight: 900,
                                            color:
                                                COLORS.text,
                                            fontSize: {
                                                xs: "1.05rem",
                                                sm: "1.2rem",
                                            },
                                        }}
                                    >
                                        My Students
                                    </Typography>

                                    <Typography
                                        sx={{
                                            mt: 0.25,
                                            color:
                                                COLORS.secondary,
                                            fontSize: {
                                                xs: "0.76rem",
                                                sm: "0.82rem",
                                            },
                                        }}
                                    >
                                        Select a student to
                                        view academic and
                                        financial information.
                                    </Typography>
                                </Box>
                            </Stack>

                            <Chip
                                icon={<Groups />}
                                label={`${children.length} ${
                                    children.length === 1
                                        ? "Student"
                                        : "Students"
                                }`}
                                size="small"
                                sx={{
                                    borderRadius:
                                        "9px",
                                    background:
                                        COLORS.primarySoft,
                                    color:
                                        COLORS.primaryDark,
                                    fontWeight: 800,

                                    "& .MuiChip-icon":
                                        {
                                            color:
                                                COLORS.primary,
                                        },
                                }}
                            />
                        </Stack>
                    </Box>


                    <TextField
                        fullWidth
                        value={searchTerm}
                        onChange={(event) =>
                            setSearchTerm(
                                event.target.value
                            )
                        }
                        placeholder="Search by student name, grade or division..."
                        size="small"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search
                                        sx={{
                                            color:
                                                COLORS.secondary,
                                            fontSize: 21,
                                        }}
                                    />
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            mb: 2,

                            "& .MuiOutlinedInput-root":
                                {
                                    minHeight: 46,
                                    borderRadius:
                                        "11px",
                                    background:
                                        COLORS.surface,

                                    "& fieldset": {
                                        borderColor:
                                            COLORS.border,
                                    },

                                    "&:hover fieldset":
                                        {
                                            borderColor:
                                                COLORS.primary,
                                        },

                                    "&.Mui-focused fieldset":
                                        {
                                            borderColor:
                                                COLORS.primary,
                                        },
                                },

                            "& input": {
                                fontSize: {
                                    xs: "0.82rem",
                                    sm: "0.88rem",
                                },
                            },
                        }}
                    />


                    {children.length === 0 ? (
                        <Paper
                            elevation={0}
                            sx={{
                                p: {
                                    xs: 3,
                                    sm: 5,
                                },
                                textAlign: "center",
                                borderRadius:
                                    "18px",
                                border:
                                    `1px solid ${COLORS.border}`,
                                background:
                                    COLORS.surface,
                            }}
                        >
                            <Box
                                sx={{
                                    width: 62,
                                    height: 62,
                                    mx: "auto",
                                    borderRadius:
                                        "16px",
                                    display:
                                        "flex",
                                    alignItems:
                                        "center",
                                    justifyContent:
                                        "center",
                                    background:
                                        COLORS.primarySoft,
                                    color:
                                        COLORS.primary,
                                }}
                            >
                                <School
                                    sx={{
                                        fontSize: 32,
                                    }}
                                />
                            </Box>

                            <Typography
                                sx={{
                                    mt: 2,
                                    fontWeight: 850,
                                    color:
                                        COLORS.text,
                                    fontSize:
                                        "1rem",
                                }}
                            >
                                No linked students
                            </Typography>

                            <Typography
                                sx={{
                                    mt: 0.6,
                                    color:
                                        COLORS.secondary,
                                    fontSize:
                                        "0.82rem",
                                }}
                            >
                                There are currently no
                                students linked to your
                                teaching profile.
                            </Typography>
                        </Paper>
                    ) : filteredChildren.length === 0 ? (
                        <Paper
                            elevation={0}
                            sx={{
                                p: 4,
                                textAlign: "center",
                                borderRadius:
                                    "18px",
                                border:
                                    `1px solid ${COLORS.border}`,
                                background:
                                    COLORS.surface,
                            }}
                        >
                            <Search
                                sx={{
                                    fontSize: 40,
                                    color:
                                        COLORS.muted,
                                }}
                            />

                            <Typography
                                sx={{
                                    mt: 1,
                                    color:
                                        COLORS.text,
                                    fontWeight: 800,
                                }}
                            >
                                No students found
                            </Typography>

                            <Typography
                                sx={{
                                    mt: 0.5,
                                    color:
                                        COLORS.secondary,
                                    fontSize:
                                        "0.8rem",
                                }}
                            >
                                Try another search term.
                            </Typography>
                        </Paper>
                    ) : (
                        <Paper
                            elevation={0}
                            sx={{
                                borderRadius:
                                    "16px",
                                background:
                                    COLORS.surface,
                                border:
                                    `1px solid ${COLORS.border}`,
                                overflow: "hidden",
                            }}
                        >
                            {filteredChildren.map(
                                (
                                    child,
                                    index
                                ) => (
                                    <React.Fragment
                                        key={
                                            child.id
                                        }
                                    >
                                        <Box
                                            onClick={() =>
                                                selectChild(
                                                    child
                                                )
                                            }
                                            sx={{
                                                display:
                                                    "flex",
                                                alignItems:
                                                    "center",
                                                gap: {
                                                    xs: 1.25,
                                                    sm: 1.75,
                                                },
                                                p: {
                                                    xs: 1.35,
                                                    sm: 1.65,
                                                },
                                                cursor:
                                                    "pointer",
                                                transition:
                                                    "all .18s ease",

                                                "&:hover":
                                                    {
                                                        background:
                                                            "#fffaf5",
                                                    },
                                            }}
                                        >
                                            <Avatar
                                                src={
                                                    child.student_admission_image ||
                                                    defaultAvatar
                                                }
                                                alt={
                                                    child.full_name
                                                }
                                                imgProps={{
                                                    onError:
                                                        (
                                                            event
                                                        ) => {
                                                            event
                                                                .target
                                                                .onerror =
                                                                null;

                                                            event
                                                                .target
                                                                .src =
                                                                defaultAvatar;
                                                        },
                                                }}
                                                sx={{
                                                    width:
                                                        {
                                                            xs: 44,
                                                            sm: 52,
                                                        },

                                                    height:
                                                        {
                                                            xs: 44,
                                                            sm: 52,
                                                        },

                                                    border:
                                                        `2px solid ${COLORS.primarySoft}`,

                                                    background:
                                                        "#f8fafc",

                                                    flexShrink:
                                                        0,
                                                }}
                                            />

                                            <Box
                                                sx={{
                                                    minWidth:
                                                        0,
                                                    flex: 1,
                                                }}
                                            >
                                                <Typography
                                                    sx={{
                                                        fontWeight:
                                                            850,
                                                        color:
                                                            COLORS.text,
                                                        fontSize:
                                                            {
                                                                xs: "0.84rem",
                                                                sm: "0.92rem",
                                                            },
                                                        overflow:
                                                            "hidden",
                                                        textOverflow:
                                                            "ellipsis",
                                                        whiteSpace:
                                                            "nowrap",
                                                    }}
                                                >
                                                    {
                                                        child.full_name
                                                    }
                                                </Typography>

                                                <Stack
                                                    direction="row"
                                                    spacing={
                                                        0.8
                                                    }
                                                    alignItems="center"
                                                    sx={{
                                                        mt: 0.35,
                                                        flexWrap:
                                                            "wrap",
                                                    }}
                                                >
                                                    <Typography
                                                        sx={{
                                                            color:
                                                                COLORS.secondary,
                                                            fontSize:
                                                                {
                                                                    xs: "0.7rem",
                                                                    sm: "0.77rem",
                                                                },
                                                        }}
                                                    >
                                                        {child.grade_class_name ||
                                                            "Student"}
                                                    </Typography>

                                                    {child.division_assigned_name && (
                                                        <>
                                                            <Typography
                                                                sx={{
                                                                    color:
                                                                        COLORS.muted,
                                                                    fontSize:
                                                                        "0.7rem",
                                                                }}
                                                            >
                                                                •
                                                            </Typography>

                                                            <Typography
                                                                sx={{
                                                                    color:
                                                                        COLORS.secondary,
                                                                    fontSize:
                                                                        {
                                                                            xs: "0.7rem",
                                                                            sm: "0.77rem",
                                                                        },
                                                                }}
                                                            >
                                                                {
                                                                    child.division_assigned_name
                                                                }
                                                            </Typography>
                                                        </>
                                                    )}
                                                </Stack>
                                            </Box>

                                            <Box
                                                sx={{
                                                    width: 32,
                                                    height: 32,
                                                    borderRadius:
                                                        "9px",
                                                    display:
                                                        "flex",
                                                    alignItems:
                                                        "center",
                                                    justifyContent:
                                                        "center",
                                                    background:
                                                        COLORS.primarySoft,
                                                    color:
                                                        COLORS.primary,
                                                    flexShrink:
                                                        0,
                                                }}
                                            >
                                                <ArrowForward
                                                    sx={{
                                                        fontSize:
                                                            18,
                                                    }}
                                                />
                                            </Box>
                                        </Box>

                                        {index <
                                            filteredChildren.length -
                                                1 && (
                                            <Divider
                                                sx={{
                                                    borderColor:
                                                        COLORS.border,
                                                }}
                                            />
                                        )}
                                    </React.Fragment>
                                )
                            )}
                        </Paper>
                    )}
                </>
            ) : (
                <>
                    <Button
                        startIcon={
                            <ArrowBack />
                        }
                        onClick={
                            handleBackToChildren
                        }
                        sx={{
                            mb: 2,
                            px: 0.5,
                            color:
                                COLORS.secondary,
                            textTransform:
                                "none",
                            fontWeight: 800,
                            fontSize:
                                "0.84rem",

                            "&:hover": {
                                background:
                                    "transparent",
                                color:
                                    COLORS.primaryDark,
                            },
                        }}
                    >
                        Back to Students
                    </Button>


                    {!showGrades &&
                    !showPayments ? (
                        <Box
                            sx={{
                                width: "100%",
                                overflow:
                                    "hidden",
                                borderRadius: {
                                    xs: "18px",
                                    md: "22px",
                                },
                                border:
                                    `1px solid ${COLORS.border}`,
                                background:
                                    COLORS.surface,
                                boxShadow:
                                    "0 12px 35px rgba(15,23,42,0.06)",
                            }}
                        >
                            <Box
                                sx={{
                                    p: {
                                        xs: 2,
                                        sm: 2.75,
                                        md: 3.25,
                                    },

                                    background:
                                        "linear-gradient(135deg, #111827 0%, #1f2937 58%, #b45309 150%)",

                                    color:
                                        COLORS.white,
                                }}
                            >
                                <Stack
                                    direction={{
                                        xs: "column",
                                        sm: "row",
                                    }}
                                    spacing={{
                                        xs: 2,
                                        sm: 2.5,
                                    }}
                                    alignItems={{
                                        xs: "center",
                                        sm: "flex-start",
                                    }}
                                >
                                    <Avatar
                                        alt={
                                            selectedChild.full_name
                                        }
                                        src={
                                            selectedChild.student_admission_image ||
                                            defaultAvatar
                                        }
                                        imgProps={{
                                            onError:
                                                (
                                                    event
                                                ) => {
                                                    event
                                                        .target
                                                        .onerror =
                                                        null;

                                                    event
                                                        .target
                                                        .src =
                                                        defaultAvatar;
                                                },
                                        }}
                                        sx={{
                                            width: {
                                                xs: 88,
                                                sm: 105,
                                                md: 118,
                                            },

                                            height: {
                                                xs: 88,
                                                sm: 105,
                                                md: 118,
                                            },

                                            border:
                                                "4px solid rgba(255,255,255,.85)",

                                            boxShadow:
                                                "0 10px 25px rgba(0,0,0,.25)",

                                            flexShrink: 0,
                                        }}
                                    />

                                    <Box
                                        sx={{
                                            minWidth:
                                                0,
                                            flex: 1,
                                            textAlign: {
                                                xs: "center",
                                                sm: "left",
                                            },
                                        }}
                                    >
                                        <Stack
                                            direction="row"
                                            spacing={1}
                                            alignItems="center"
                                            justifyContent={{
                                                xs: "center",
                                                sm: "flex-start",
                                            }}
                                            flexWrap="wrap"
                                        >
                                            <Chip
                                                icon={
                                                    <Person />
                                                }
                                                label="Student Profile"
                                                size="small"
                                                sx={{
                                                    height:
                                                        27,
                                                    background:
                                                        "rgba(255,255,255,.12)",
                                                    color:
                                                        COLORS.white,
                                                    border:
                                                        "1px solid rgba(255,255,255,.18)",
                                                    fontWeight:
                                                        750,

                                                    "& .MuiChip-icon":
                                                        {
                                                            color:
                                                                "#f6c58b",
                                                        },
                                                }}
                                            />
                                        </Stack>

                                        <Typography
                                            sx={{
                                                mt: 1,
                                                fontWeight:
                                                    900,
                                                fontSize:
                                                    {
                                                        xs: "1.3rem",
                                                        sm: "1.65rem",
                                                        md: "1.95rem",
                                                    },
                                                lineHeight:
                                                    1.2,
                                                wordBreak:
                                                    "break-word",
                                            }}
                                        >
                                            {
                                                selectedChild.full_name
                                            }
                                        </Typography>

                                        <Typography
                                            sx={{
                                                mt: 0.7,
                                                color:
                                                    "#d1d5db",
                                                fontSize:
                                                    {
                                                        xs: "0.78rem",
                                                        sm: "0.84rem",
                                                    },
                                            }}
                                        >
                                            {selectedChild.grade_class_name ||
                                                "Student"}{" "}
                                            {selectedChild.division_assigned_name
                                                ? `• ${selectedChild.division_assigned_name}`
                                                : ""}
                                        </Typography>

                                        <Stack
                                            direction={{
                                                xs: "column",
                                                sm: "row",
                                            }}
                                            spacing={1}
                                            sx={{
                                                mt: 2,
                                                justifyContent:
                                                    {
                                                        xs: "center",
                                                        sm: "flex-start",
                                                    },
                                            }}
                                        >
                                            <Button
                                                variant="contained"
                                                startIcon={
                                                    <MenuBook />
                                                }
                                                onClick={() =>
                                                    setShowGrades(
                                                        true
                                                    )
                                                }
                                                sx={{
                                                    minHeight:
                                                        40,
                                                    px: 2,
                                                    borderRadius:
                                                        "10px",
                                                    textTransform:
                                                        "none",
                                                    fontWeight:
                                                        850,
                                                    color:
                                                        COLORS.text,
                                                    background:
                                                        "#ffffff",

                                                    "&:hover":
                                                        {
                                                            background:
                                                                "#f8fafc",
                                                        },
                                                }}
                                            >
                                                View Grades
                                            </Button>

                                            <Button
                                                variant="contained"
                                                startIcon={
                                                    <Payments />
                                                }
                                                onClick={() =>
                                                    setShowPayments(
                                                        true
                                                    )
                                                }
                                                sx={{
                                                    minHeight:
                                                        40,
                                                    px: 2,
                                                    borderRadius:
                                                        "10px",
                                                    textTransform:
                                                        "none",
                                                    fontWeight:
                                                        850,
                                                    color:
                                                        COLORS.white,
                                                    background:
                                                        "#2563eb",

                                                    "&:hover":
                                                        {
                                                            background:
                                                                "#1d4ed8",
                                                        },
                                                }}
                                            >
                                                Payment Transactions
                                            </Button>
                                        </Stack>
                                    </Box>
                                </Stack>
                            </Box>


                            <Box
                                sx={{
                                    p: {
                                        xs: 1.5,
                                        sm: 2.25,
                                        md: 3,
                                    },

                                    background:
                                        COLORS.background,
                                }}
                            >
                                <SectionCard
                                    title="Student Information"
                                    subtitle="Basic academic assignment"
                                    icon={
                                        <School />
                                    }
                                    fullWidth
                                >
                                    <InfoRow
                                        label="Grade Class"
                                        value={
                                            selectedChild.grade_class_name
                                        }
                                        icon={
                                            <School
                                                sx={{
                                                    fontSize:
                                                        17,
                                                }}
                                            />
                                        }
                                    />

                                    <InfoRow
                                        label="Division"
                                        value={
                                            selectedChild.division_assigned_name
                                        }
                                        icon={
                                            <Groups
                                                sx={{
                                                    fontSize:
                                                        17,
                                                }}
                                            />
                                        }
                                    />
                                </SectionCard>


                                <Box
                                    sx={{
                                        display:
                                            "flex",
                                        flexWrap:
                                            "wrap",
                                        gap: 2,
                                        mt: 2,
                                    }}
                                >
                                    <SectionCard
                                        title="Attendance Summary"
                                        subtitle="Current attendance overview"
                                        icon={
                                            <EventAvailable />
                                        }
                                    >
                                        <Box
                                            sx={{
                                                display:
                                                    "grid",
                                                gridTemplateColumns:
                                                    {
                                                        xs: "repeat(3, minmax(0, 1fr))",
                                                        sm: "repeat(3, minmax(0, 1fr))",
                                                    },
                                                gap: 1,
                                            }}
                                        >
                                            <StatCard
                                                label="Present"
                                                value={
                                                    selectedChild.total_present ??
                                                    0
                                                }
                                                icon={
                                                    <CheckCircle
                                                        sx={{
                                                            fontSize:
                                                                17,
                                                        }}
                                                    />
                                                }
                                                color={
                                                    COLORS.green
                                                }
                                                background={
                                                    COLORS.greenSoft
                                                }
                                            />

                                            <StatCard
                                                label="Absent"
                                                value={
                                                    selectedChild.total_absent ??
                                                    0
                                                }
                                                icon={
                                                    <Cancel
                                                        sx={{
                                                            fontSize:
                                                                17,
                                                        }}
                                                    />
                                                }
                                                color={
                                                    COLORS.red
                                                }
                                                background={
                                                    COLORS.redSoft
                                                }
                                            />

                                            <StatCard
                                                label="Late"
                                                value={
                                                    selectedChild.total_late ??
                                                    0
                                                }
                                                icon={
                                                    <AccessTime
                                                        sx={{
                                                            fontSize:
                                                                17,
                                                        }}
                                                    />
                                                }
                                                color={
                                                    COLORS.amber
                                                }
                                                background={
                                                    COLORS.amberSoft
                                                }
                                            />
                                        </Box>
                                    </SectionCard>


                                    <SectionCard
                                        title="Fees Overview"
                                        subtitle="Student financial summary"
                                        icon={
                                            <AccountBalanceWallet />
                                        }
                                    >
                                        <Box
                                            sx={{
                                                display:
                                                    "grid",
                                                gridTemplateColumns:
                                                    {
                                                        xs: "repeat(3, minmax(0, 1fr))",
                                                        sm: "repeat(3, minmax(0, 1fr))",
                                                    },
                                                gap: 1,
                                            }}
                                        >
                                            <StatCard
                                                label="Total Fee"
                                                value={formatCurrency(
                                                    selectedChild.total_fee
                                                )}
                                                icon={
                                                    <AccountBalanceWallet
                                                        sx={{
                                                            fontSize:
                                                                17,
                                                        }}
                                                    />
                                                }
                                                color={
                                                    COLORS.text
                                                }
                                                background={
                                                    "#f8fafc"
                                                }
                                            />

                                            <StatCard
                                                label="Paid"
                                                value={formatCurrency(
                                                    selectedChild.amount_paid
                                                )}
                                                icon={
                                                    <CheckCircle
                                                        sx={{
                                                            fontSize:
                                                                17,
                                                        }}
                                                    />
                                                }
                                                color={
                                                    COLORS.green
                                                }
                                                background={
                                                    COLORS.greenSoft
                                                }
                                            />

                                            <StatCard
                                                label="Balance"
                                                value={formatCurrency(
                                                    selectedChild.balance_fee
                                                )}
                                                icon={
                                                    <AccountBalanceWallet
                                                        sx={{
                                                            fontSize:
                                                                17,
                                                        }}
                                                    />
                                                }
                                                color={
                                                    COLORS.red
                                                }
                                                background={
                                                    COLORS.redSoft
                                                }
                                            />
                                        </Box>
                                    </SectionCard>
                                </Box>


                                <Box
                                    sx={{
                                        mt: 2,
                                    }}
                                >
                                    <SectionCard
                                        title="Complaints"
                                        subtitle={
                                            selectedChild.complaints
                                                ?.length
                                                ? `${selectedChild.complaints.length} recorded`
                                                : "Student conduct records"
                                        }
                                        icon={
                                            <ReportProblem />
                                        }
                                        fullWidth
                                    >
                                        {selectedChild.complaints
                                            ?.length > 0 ? (
                                            <>
                                                <Box
                                                    sx={{
                                                        display:
                                                            "flex",
                                                        alignItems:
                                                            "center",
                                                        justifyContent:
                                                            "space-between",
                                                        gap: 1,
                                                        px: 1,
                                                        py: 0.7,
                                                        borderRadius:
                                                            "11px",
                                                        background:
                                                            COLORS.primarySoft,
                                                        border:
                                                            `1px solid ${COLORS.border}`,
                                                    }}
                                                >
                                                    <IconButton
                                                        onClick={() =>
                                                            handlePrev(
                                                                selectedChild
                                                            )
                                                        }
                                                        disabled={
                                                            currentComplaintIndex(
                                                                selectedChild
                                                            ) ===
                                                            0
                                                        }
                                                        size="small"
                                                        sx={{
                                                            color:
                                                                COLORS.text,
                                                        }}
                                                    >
                                                        <ArrowBackIosNew
                                                            fontSize="small"
                                                        />
                                                    </IconButton>

                                                    <Typography
                                                        sx={{
                                                            color:
                                                                COLORS.text,
                                                            fontWeight:
                                                                800,
                                                            fontSize:
                                                                "0.78rem",
                                                        }}
                                                    >
                                                        Complaint{" "}
                                                        {currentComplaintIndex(
                                                            selectedChild
                                                        ) +
                                                            1}{" "}
                                                        of{" "}
                                                        {
                                                            selectedChild
                                                                .complaints
                                                                .length
                                                        }
                                                    </Typography>

                                                    <IconButton
                                                        onClick={() =>
                                                            handleNext(
                                                                selectedChild,
                                                                selectedChild
                                                                    .complaints
                                                                    .length
                                                            )
                                                        }
                                                        disabled={
                                                            currentComplaintIndex(
                                                                selectedChild
                                                            ) ===
                                                            selectedChild
                                                                .complaints
                                                                .length -
                                                                1
                                                        }
                                                        size="small"
                                                        sx={{
                                                            color:
                                                                COLORS.text,
                                                        }}
                                                    >
                                                        <ArrowForward
                                                            fontSize="small"
                                                        />
                                                    </IconButton>
                                                </Box>


                                                <Box
                                                    sx={{
                                                        maxHeight:
                                                            270,
                                                        overflowY:
                                                            "auto",
                                                        pr: 0.5,
                                                    }}
                                                >
                                                    {selectedChild.complaints.map(
                                                        (
                                                            complaint,
                                                            index
                                                        ) => {
                                                            const isActive =
                                                                index ===
                                                                currentComplaintIndex(
                                                                    selectedChild
                                                                );

                                                            return (
                                                                <Box
                                                                    key={
                                                                        complaint.id
                                                                    }
                                                                    sx={{
                                                                        p:
                                                                            {
                                                                                xs: 1.5,
                                                                                sm: 1.75,
                                                                            },
                                                                        mb: 1,
                                                                        borderRadius:
                                                                            "12px",
                                                                        background:
                                                                            isActive
                                                                                ? "#fffaf5"
                                                                                : COLORS.surface,
                                                                        border:
                                                                            `1px solid ${
                                                                                isActive
                                                                                    ? "#f2c08a"
                                                                                    : COLORS.border
                                                                            }`,
                                                                        transition:
                                                                            "all .2s ease",
                                                                    }}
                                                                >
                                                                    <Stack
                                                                        direction="row"
                                                                        spacing={
                                                                            1
                                                                        }
                                                                        alignItems="flex-start"
                                                                    >
                                                                        <Box
                                                                            sx={{
                                                                                width: 30,
                                                                                height: 30,
                                                                                borderRadius:
                                                                                    "8px",
                                                                                display:
                                                                                    "flex",
                                                                                alignItems:
                                                                                    "center",
                                                                                justifyContent:
                                                                                    "center",
                                                                                background:
                                                                                    isActive
                                                                                        ? COLORS.primarySoft
                                                                                        : "#f8fafc",
                                                                                color:
                                                                                    isActive
                                                                                        ? COLORS.primary
                                                                                        : COLORS.secondary,
                                                                                flexShrink: 0,
                                                                            }}
                                                                        >
                                                                            <ReportProblem
                                                                                sx={{
                                                                                    fontSize:
                                                                                        17,
                                                                                }}
                                                                            />
                                                                        </Box>

                                                                        <Box
                                                                            sx={{
                                                                                minWidth:
                                                                                    0,
                                                                                flex: 1,
                                                                            }}
                                                                        >
                                                                            <Typography
                                                                                sx={{
                                                                                    color:
                                                                                        COLORS.text,
                                                                                    fontWeight:
                                                                                        850,
                                                                                    fontSize:
                                                                                        {
                                                                                            xs: "0.84rem",
                                                                                            sm: "0.9rem",
                                                                                        },
                                                                                    lineHeight:
                                                                                        1.35,
                                                                                }}
                                                                            >
                                                                                {
                                                                                    complaint.title
                                                                                }
                                                                            </Typography>

                                                                            {isActive && (
                                                                                <Typography
                                                                                    sx={{
                                                                                        mt: 0.8,
                                                                                        color:
                                                                                            COLORS.secondary,
                                                                                        fontSize:
                                                                                            {
                                                                                                xs: "0.76rem",
                                                                                                sm: "0.83rem",
                                                                                            },
                                                                                        lineHeight:
                                                                                            1.65,
                                                                                        whiteSpace:
                                                                                            "pre-wrap",
                                                                                    }}
                                                                                >
                                                                                    {
                                                                                        complaint.description
                                                                                    }
                                                                                </Typography>
                                                                            )}
                                                                        </Box>
                                                                    </Stack>
                                                                </Box>
                                                            );
                                                        }
                                                    )}
                                                </Box>
                                            </>
                                        ) : (
                                            <Box
                                                sx={{
                                                    py: 3,
                                                    textAlign:
                                                        "center",
                                                    borderRadius:
                                                        "12px",
                                                    background:
                                                        "#f8fafc",
                                                    border:
                                                        `1px dashed ${COLORS.borderStrong}`,
                                                }}
                                            >
                                                <CheckCircle
                                                    sx={{
                                                        fontSize:
                                                            30,
                                                        color:
                                                            COLORS.green,
                                                    }}
                                                />

                                                <Typography
                                                    sx={{
                                                        mt: 0.75,
                                                        color:
                                                            COLORS.text,
                                                        fontWeight:
                                                            800,
                                                        fontSize:
                                                            "0.85rem",
                                                    }}
                                                >
                                                    No complaints recorded
                                                </Typography>

                                                <Typography
                                                    sx={{
                                                        mt: 0.3,
                                                        color:
                                                            COLORS.secondary,
                                                        fontSize:
                                                            "0.75rem",
                                                    }}
                                                >
                                                    No student conduct
                                                    complaints are currently
                                                    available.
                                                </Typography>
                                            </Box>
                                        )}
                                    </SectionCard>
                                </Box>
                            </Box>
                        </Box>
                    ) : showGrades ? (
                        <ParentsStudentGrades
                            selectedChild={
                                selectedChild
                            }
                            onBack={() =>
                                setShowGrades(
                                    false
                                )
                            }
                        />
                    ) : (
                        <TransActionPerStudent
                            selectedChild={
                                selectedChild
                            }
                            onBack={() =>
                                setShowPayments(
                                    false
                                )
                            }
                        />
                    )}
                </>
            )}
        </Box>
    );
};


export default LinkStudents;







// import React, { useEffect, useState } from "react";
// import api from "../api/api";
// import ParentsStudentGrades from "../pages/Students/ParentsStudentGrades";
// import defaultAvatar from "./maleee.jpg";
// import TransActionPerStudent from "../SchoolDashApp/DailyTransaction/TransActionPerStudent";

// import {
// Avatar,
// Box,
// Button,
// Typography,
// TextField,
// Divider,
// IconButton,
// InputAdornment
// } from "@mui/material";

// import {
// Search,
// ArrowBack,
// ArrowForward,
// ArrowBackIosNew,
// School,
// AccountBalanceWallet,
// EventAvailable,
// ReportProblem,
// ArrowBack as BackIcon
// } from "@mui/icons-material";

// const COLORS = {
// black: "#111111",
// darkBlack: "#1c1c1c",
// orange: "#e87519",
// deepOrange: "#bd4e00",
// camel: "#c89b5b",
// lightCamel: "#f4e4ce",
// cream: "#fffaf3",
// white: "#ffffff",
// gray: "#6b7280",
// lightGray: "#f5f5f5",
// border: "rgba(17,17,17,0.10)",
// green: "#2e7d32",
// red: "#c62828",
// blue: "#1565c0"
// };

// const formatCurrency = (value) => {
// const number = parseFloat(value);


// if (isNaN(number)) {
//     return "$0.00";
// }

// return `$${number.toFixed(2)}`;


// };

// const LinkStudents = () => {
// const [children, setChildren] = useState([]);
// const [filteredChildren, setFilteredChildren] = useState([]);
// const [selectedChild, setSelectedChild] = useState(null);
// const [showGrades, setShowGrades] = useState(false);
// const [showPayments, setShowPayments] = useState(false);
// const [searchTerm, setSearchTerm] = useState("");
// const [complaintIndices, setComplaintIndices] = useState({});


// const token = localStorage.getItem("access");

// useEffect(() => {
//     const fetchLinkedStudents = async () => {
//         try {
//             if (!token) return;

//             const res = await api.get(
//                 "/admissions/my-linked-students/",
//                 {
//                     headers: {
//                         Authorization: `Bearer ${token}`
//                     }
//                 }
//             );

//             setChildren(res.data);
//             setFilteredChildren(res.data);
//         } catch (error) {
//             console.error(
//                 "Error fetching linked students:",
//                 error
//             );
//         }
//     };

//     fetchLinkedStudents();
// }, [token]);

// const handleSearch = (event) => {
//     const value = event.target.value.toLowerCase();

//     setSearchTerm(value);

//     const filtered = children.filter((child) =>
//         child.full_name
//             ?.toLowerCase()
//             .includes(value)
//     );

//     setFilteredChildren(filtered);
// };

// const selectChild = (child) => {
//     setSelectedChild(child);
//     setShowGrades(false);
//     setShowPayments(false);
// };

// const InfoRow = ({ label, value }) => (
//     <Box
//         sx={{
//             display: "flex",
//             justifyContent: "space-between",
//             alignItems: "flex-start",
//             gap: 2,
//             py: 1,
//             borderBottom: `1px solid ${COLORS.border}`,
//             "&:last-child": {
//                 borderBottom: "none"
//             }
//         }}
//     >
//         <Typography
//             sx={{
//                 color: COLORS.gray,
//                 fontSize: {
//                     xs: "0.75rem",
//                     sm: "0.85rem"
//                 },
//                 fontWeight: 700
//             }}
//         >
//             {label}
//         </Typography>

//         <Typography
//             sx={{
//                 color: COLORS.black,
//                 fontSize: {
//                     xs: "0.78rem",
//                     sm: "0.88rem"
//                 },
//                 fontWeight: 800,
//                 textAlign: "right",
//                 wordBreak: "break-word"
//             }}
//         >
//             {value || "N/A"}
//         </Typography>
//     </Box>
// );

// const SectionCard = ({
//     title,
//     icon,
//     children,
//     fullWidth = false
// }) => (
//     <Box
//         sx={{
//             flex: fullWidth
//                 ? "1 1 100%"
//                 : "1 1 300px",
//             minWidth: {
//                 xs: "100%",
//                 sm: 280
//             },
//             p: {
//                 xs: 1.8,
//                 sm: 2.5,
//                 md: 3
//             },
//             borderRadius: {
//                 xs: "18px",
//                 md: "24px"
//             },
//             background:
//                 "linear-gradient(145deg, #ffffff 0%, #fff8ee 100%)",
//             border: `1px solid ${COLORS.border}`,
//             boxShadow:
//                 "0 10px 30px rgba(17,17,17,0.07)",
//             display: "flex",
//             flexDirection: "column",
//             gap: 2
//         }}
//     >
//         <Box
//             sx={{
//                 display: "flex",
//                 alignItems: "center",
//                 gap: 1.2
//             }}
//         >
//             <Box
//                 sx={{
//                     width: 38,
//                     height: 38,
//                     borderRadius: "12px",
//                     display: "flex",
//                     alignItems: "center",
//                     justifyContent: "center",
//                     color: COLORS.white,
//                     background:
//                         "linear-gradient(135deg, #111111, #e87519)",
//                     flexShrink: 0
//                 }}
//             >
//                 {icon}
//             </Box>

//             <Typography
//                 sx={{
//                     fontWeight: 900,
//                     color: COLORS.black,
//                     fontSize: {
//                         xs: "0.95rem",
//                         sm: "1.05rem"
//                     }
//                 }}
//             >
//                 {title}
//             </Typography>
//         </Box>

//         {children}
//     </Box>
// );

// const StatGrid = ({ items }) => (
//     <Box
//         sx={{
//             display: "grid",
//             gridTemplateColumns: {
//                 xs: "repeat(2, minmax(0, 1fr))",
//                 sm: `repeat(${items.length}, minmax(0, 1fr))`
//             },
//             gap: {
//                 xs: 1,
//                 sm: 1.5
//             }
//         }}
//     >
//         {items.map(({ label, value, color }) => (
//             <Box
//                 key={label}
//                 sx={{
//                     p: {
//                         xs: 1.2,
//                         sm: 1.5
//                     },
//                     minHeight: {
//                         xs: 82,
//                         sm: 95
//                     },
//                     borderRadius: "15px",
//                     textAlign: "center",
//                     display: "flex",
//                     flexDirection: "column",
//                     justifyContent: "center",
//                     background: COLORS.white,
//                     border: `1px solid ${COLORS.border}`,
//                     transition:
//                         "all 0.25s ease",
//                     "&:hover": {
//                         transform:
//                             "translateY(-3px)",
//                         boxShadow:
//                             "0 8px 20px rgba(0,0,0,0.08)"
//                     }
//                 }}
//             >
//                 <Typography
//                     sx={{
//                         color: COLORS.gray,
//                         fontSize: {
//                             xs: "0.68rem",
//                             sm: "0.75rem"
//                         },
//                         fontWeight: 700,
//                         mb: 0.5
//                     }}
//                 >
//                     {label}
//                 </Typography>

//                 <Typography
//                     sx={{
//                         color: color || COLORS.black,
//                         fontSize: {
//                             xs: "0.9rem",
//                             sm: "1.05rem"
//                         },
//                         fontWeight: 900,
//                         wordBreak: "break-word"
//                     }}
//                 >
//                     {value}
//                 </Typography>
//             </Box>
//         ))}
//     </Box>
// );

// const setChildComplaintIndex = (
//     childId,
//     newIndex
// ) => {
//     setComplaintIndices((prev) => ({
//         ...prev,
//         [childId]: newIndex
//     }));
// };

// const currentComplaintIndex = (child) =>
//     complaintIndices[child.id] ?? 0;

// const handlePrev = (child) => {
//     const index =
//         currentComplaintIndex(child);

//     if (index > 0) {
//         setChildComplaintIndex(
//             child.id,
//             index - 1
//         );
//     }
// };

// const handleNext = (child, total) => {
//     const index =
//         currentComplaintIndex(child);

//     if (index < total - 1) {
//         setChildComplaintIndex(
//             child.id,
//             index + 1
//         );
//     }
// };

// return (
//     <Box
//         sx={{
//             width: "100%",
//             minHeight: "100%",
//             px: {
//                 xs: 0,
//                 sm: 1,
//                 md: 2
//             },
//             py: {
//                 xs: 1,
//                 sm: 2
//             }
//         }}
//     >
//         {!selectedChild ? (
//             <>
//                 {/* CHILDREN LIST HEADER */}
//                 <Box
//                     sx={{
//                         textAlign: "center",
//                         mb: 3
//                     }}
//                 >
//                     <Typography
//                         sx={{
//                             fontWeight: 900,
//                             color: COLORS.black,
//                             fontSize: {
//                                 xs: "1.35rem",
//                                 sm: "1.7rem",
//                                 md: "2rem"
//                             }
//                         }}
//                     >
//                         My Children
//                     </Typography>

//                     <Typography
//                         sx={{
//                             mt: 0.7,
//                             color: COLORS.gray,
//                             fontSize: {
//                                 xs: "0.8rem",
//                                 sm: "0.9rem"
//                             }
//                         }}
//                     >
//                         Select a student to view academic
//                         information and performance.
//                     </Typography>
//                 </Box>

//                 {/* SEARCH */}
//                 <TextField
//                     fullWidth
//                     value={searchTerm}
//                     onChange={handleSearch}
//                     placeholder="Search student by name..."
//                     size="small"
//                     InputProps={{
//                         startAdornment: (
//                             <InputAdornment position="start">
//                                 <Search
//                                     sx={{
//                                         color: COLORS.orange
//                                     }}
//                                 />
//                             </InputAdornment>
//                         )
//                     }}
//                     sx={{
//                         mb: 2.5,
//                         "& .MuiOutlinedInput-root": {
//                             borderRadius: "15px",
//                             backgroundColor:
//                                 COLORS.white,
//                             "& fieldset": {
//                                 borderColor:
//                                     COLORS.border
//                             },
//                             "&:hover fieldset": {
//                                 borderColor:
//                                     COLORS.orange
//                             },
//                             "&.Mui-focused fieldset": {
//                                 borderColor:
//                                     COLORS.orange
//                             }
//                         },
//                         "& input": {
//                             fontSize: {
//                                 xs: "0.85rem",
//                                 sm: "0.95rem"
//                             }
//                         }
//                     }}
//                 />

//                 {/* CHILDREN LIST */}
//                 <Box
//                     sx={{
//                         maxHeight: 450,
//                         overflowY: "auto",
//                         borderRadius: "20px",
//                         background:
//                             "linear-gradient(145deg, #ffffff, #fff8ef)",
//                         border: `1px solid ${COLORS.border}`,
//                         boxShadow:
//                             "0 10px 30px rgba(0,0,0,0.06)",
//                         p: 1
//                     }}
//                 >
//                     {filteredChildren.length > 0 ? (
//                         filteredChildren.map(
//                             (child, index) => (
//                                 <React.Fragment
//                                     key={child.id}
//                                 >
//                                     <Box
//                                         onClick={() =>
//                                             selectChild(
//                                                 child
//                                             )
//                                         }
//                                         sx={{
//                                             display: "flex",
//                                             alignItems:
//                                                 "center",
//                                             gap: 1.5,
//                                             p: {
//                                                 xs: 1.3,
//                                                 sm: 1.8
//                                             },
//                                             borderRadius:
//                                                 "15px",
//                                             cursor: "pointer",
//                                             transition:
//                                                 "all 0.25s ease",
//                                             "&:hover": {
//                                                 background:
//                                                     COLORS.lightCamel,
//                                                 transform:
//                                                     "translateX(4px)"
//                                             }
//                                         }}
//                                     >
//                                        <Avatar
//                                           src={child.student_admission_image || defaultAvatar}
//                                           alt={child.full_name}
//                                           imgProps={{
//                                             onError: (event) => {
//                                               event.target.onerror = null;
//                                               event.target.src = defaultAvatar;
//                                             }
//                                           }}
//                                           sx={{
//                                             width: { xs: 45, sm: 52 },
//                                             height: { xs: 45, sm: 52 },
//                                             border: `3px solid ${COLORS.orange}`,
//                                             flexShrink: 0
//                                           }}
//                                         />

//                                         <Box
//                                             sx={{
//                                                 minWidth: 0,
//                                                 flex: 1
//                                             }}
//                                         >
//                                             <Typography
//                                                 sx={{
//                                                     fontWeight: 900,
//                                                     color: COLORS.black,
//                                                     fontSize: {
//                                                         xs: "0.85rem",
//                                                         sm: "0.95rem"
//                                                     },
//                                                     overflow:
//                                                         "hidden",
//                                                     textOverflow:
//                                                         "ellipsis",
//                                                     whiteSpace:
//                                                         "nowrap"
//                                                 }}
//                                             >
//                                                 {
//                                                     child.full_name
//                                                 }
//                                             </Typography>

//                                             <Typography
//                                                 sx={{
//                                                     color: COLORS.gray,
//                                                     fontSize: {
//                                                         xs: "0.7rem",
//                                                         sm: "0.8rem"
//                                                     },
//                                                     mt: 0.3
//                                                 }}
//                                             >
//                                                 {child.grade_class_name ||
//                                                     "Student"}
//                                             </Typography>
//                                         </Box>

//                                         <ArrowForward
//                                             sx={{
//                                                 color: COLORS.orange,
//                                                 flexShrink: 0
//                                             }}
//                                         />
//                                     </Box>

//                                     {index <
//                                         filteredChildren.length -
//                                             1 && (
//                                         <Divider
//                                             sx={{
//                                                 mx: 1
//                                             }}
//                                         />
//                                     )}
//                                 </React.Fragment>
//                             )
//                         )
//                     ) : (
//                         <Box
//                             sx={{
//                                 py: 6,
//                                 textAlign: "center"
//                             }}
//                         >
//                             <School
//                                 sx={{
//                                     fontSize: 45,
//                                     color: COLORS.camel,
//                                     mb: 1
//                                 }}
//                             />

//                             <Typography
//                                 sx={{
//                                     color: COLORS.gray,
//                                     fontWeight: 700
//                                 }}
//                             >
//                                 No matching children found.
//                             </Typography>
//                         </Box>
//                     )}
//                 </Box>
//             </>
//         ) : (
//             <>
//                 {/* BACK BUTTON */}
//                 <Button
//                     startIcon={<BackIcon />}
//                     onClick={() => {
//                         setSelectedChild(null);
//                         setShowGrades(false);
//                         setShowPayments(false);
//                     }}
//                     sx={{
//                         mb: 2.5,
//                         color: COLORS.black,
//                         fontWeight: 800,
//                         borderRadius: "12px",
//                         px: 2,
//                         "&:hover": {
//                             backgroundColor:
//                                 COLORS.lightCamel
//                         }
//                     }}
//                 >
//                     Back to Children
//                 </Button>

//                 {/* {!showGrades ? ( */}
//                 {!showGrades && !showPayments ? (
//                     <Box
//                         sx={{
//                             width: "100%",
//                             borderRadius: {
//                                 xs: "20px",
//                                 md: "28px"
//                             },
//                             overflow: "hidden",
//                             background:
//                                 "linear-gradient(135deg, #111111 0%, #292929 42%, #e87519 100%)",
//                             boxShadow:
//                                 "0 18px 45px rgba(0,0,0,0.18)"
//                         }}
//                     >
//                         {/* PROFILE HEADER */}
//                         <Box
//                             sx={{
//                                 p: {
//                                     xs: 2,
//                                     sm: 3,
//                                     md: 4
//                                 },
//                                 display: "flex",
//                                 flexDirection: {
//                                     xs: "column",
//                                     sm: "row"
//                                 },
//                                 alignItems: "center",
//                                 gap: {
//                                     xs: 2,
//                                     sm: 3
//                                 },
//                                 textAlign: {
//                                     xs: "center",
//                                     sm: "left"
//                                 }
//                             }}
//                         >
//                             <Avatar
//                                 alt={
//                                     selectedChild.full_name
//                                 }
//                                 src={
//                                     selectedChild.student_admission_image  ||
//                                     defaultAvatar
//                                 }
//                                 imgProps={{
//                                     onError: (
//                                         event
//                                     ) => {
//                                         event.target.onerror =
//                                             null;
//                                         event.target.src =
//                                             defaultAvatar;
//                                     }
//                                 }}
//                                 sx={{
//                                     width: {
//                                         xs: 110,
//                                         sm: 135,
//                                         md: 155
//                                     },
//                                     height: {
//                                         xs: 110,
//                                         sm: 135,
//                                         md: 155
//                                     },
//                                     border: `5px solid ${COLORS.camel}`,
//                                     boxShadow:
//                                         "0 10px 25px rgba(0,0,0,0.35)",
//                                     flexShrink: 0
//                                 }}
//                             />

//                             <Box
//                                 sx={{
//                                     minWidth: 0,
//                                     flex: 1
//                                 }}
//                             >
//                                 <Typography
//                                     sx={{
//                                         color: COLORS.white,
//                                         fontWeight: 900,
//                                         fontSize: {
//                                             xs: "1.3rem",
//                                             sm: "1.8rem",
//                                             md: "2.2rem"
//                                         },
//                                         lineHeight: 1.2,
//                                         wordBreak:
//                                             "break-word"
//                                     }}
//                                 >
//                                     {
//                                         selectedChild.full_name
//                                     }
//                                 </Typography>

//                                 <Typography
//                                     sx={{
//                                         color: "#f7d7b5",
//                                         mt: 0.8,
//                                         fontSize: {
//                                             xs: "0.8rem",
//                                             sm: "0.95rem"
//                                         }
//                                     }}
//                                 >
//                                     Student Profile
//                                 </Typography>

//                                 <Button
//                                     variant="contained"
//                                     onClick={() =>
//                                         setShowGrades(
//                                             true
//                                         )
//                                     }
//                                     sx={{
//                                         mt: 2,
//                                         borderRadius:
//                                             "12px",
//                                         px: 2.5,
//                                         fontWeight: 900,
//                                         color: COLORS.black,
//                                         background:
//                                             "linear-gradient(135deg, #f4c27a, #ffffff)",
//                                         "&:hover": {
//                                             background:
//                                                 "linear-gradient(135deg, #ffffff, #f4c27a)"
//                                         }
//                                     }}
//                                 >
//                                     View Grades
//                                 </Button>
//                                 <Button
//                                 variant="contained"
//                                 onClick={() =>
//                                     setShowPayments(true)
//                                 }
//                                 sx={{
//                                     mt: 2,
//                                     ml: 2,
//                                     borderRadius: "12px",
//                                     px: 2.5,
//                                     fontWeight: 900,
//                                     color: COLORS.white,
//                                     background:
//                                         "linear-gradient(135deg,#1565c0,#42a5f5)",
//                                     "&:hover": {
//                                         background:
//                                             "linear-gradient(135deg,#0d47a1,#1976d2)"
//                                     }
//                                 }}
//                             >
//                                 Payment Transactions
//                             </Button>
//                             </Box>
//                         </Box>

//                         {/* PROFILE CONTENT */}
//                         <Box
//                             sx={{
//                                 background:
//                                     "linear-gradient(135deg, #fffaf3 0%, #ffffff 55%, #f7eadb 100%)",
//                                 p: {
//                                     xs: 1.5,
//                                     sm: 2.5,
//                                     md: 4
//                                 }
//                             }}
//                         >
//                             {/* BASIC INFORMATION */}
//                             <Box
//                                 sx={{
//                                     p: {
//                                         xs: 1.8,
//                                         sm: 2.5
//                                     },
//                                     mb: 3,
//                                     borderRadius:
//                                         "20px",
//                                     background:
//                                         COLORS.white,
//                                     border: `1px solid ${COLORS.border}`,
//                                     boxShadow:
//                                         "0 8px 25px rgba(0,0,0,0.06)"
//                                 }}
//                             >
//                                 <Typography
//                                     sx={{
//                                         fontWeight: 900,
//                                         color: COLORS.black,
//                                         mb: 1
//                                     }}
//                                 >
//                                     Student Information
//                                 </Typography>

//                                 <InfoRow
//                                     label="Grade Class"
//                                     value={
//                                         selectedChild.grade_class_name
//                                     }
//                                 />

//                                 <InfoRow
//                                     label="Division"
//                                     value={
//                                         selectedChild.division_assigned_name
//                                     }
//                                 />
//                             </Box>

//                             {/* SUMMARY CARDS */}
//                             <Box
//                                 sx={{
//                                     display: "flex",
//                                     flexWrap: "wrap",
//                                     gap: 2,
//                                     mb: 3
//                                 }}
//                             >
//                                 <SectionCard
//                                     title="Attendance Summary"
//                                     icon={
//                                         <EventAvailable />
//                                     }
//                                 >
//                                     <StatGrid
//                                         items={[
//                                             {
//                                                 label: "Present",
//                                                 value:
//                                                     selectedChild.total_present ??
//                                                     0,
//                                                 color:
//                                                     COLORS.green
//                                             },
//                                             {
//                                                 label: "Absent",
//                                                 value:
//                                                     selectedChild.total_absent ??
//                                                     0,
//                                                 color:
//                                                     COLORS.red
//                                             },
//                                             {
//                                                 label: "Late",
//                                                 value:
//                                                     selectedChild.total_late ??
//                                                     0,
//                                                 color:
//                                                     COLORS.orange
//                                             }
//                                         ]}
//                                     />
//                                 </SectionCard>

//                                 <SectionCard
//                                     title="Fees"
//                                     icon={
//                                         <AccountBalanceWallet />
//                                     }
//                                 >
//                                     <StatGrid
//                                         items={[
//                                             {
//                                                 label: "Total Fee",
//                                                 value:
//                                                     formatCurrency(
//                                                         selectedChild.total_fee
//                                                     ),
//                                                 color:
//                                                     COLORS.black
//                                             },
//                                             {
//                                                 label: "Amount Paid",
//                                                 value:
//                                                     formatCurrency(
//                                                         selectedChild.amount_paid
//                                                     ),
//                                                 color:
//                                                     COLORS.green
//                                             },
//                                             {
//                                                 label: "Balance",
//                                                 value:
//                                                     formatCurrency(
//                                                         selectedChild.balance_fee
//                                                     ),
//                                                 color:
//                                                     COLORS.red
//                                             }
//                                         ]}
//                                     />
//                                 </SectionCard>
//                             </Box>

//                             {/* COMPLAINTS */}
//                             <SectionCard
//                                 title="Complaints"
//                                 icon={
//                                     <ReportProblem />
//                                 }
//                                 fullWidth
//                             >
//                                 {selectedChild.complaints
//                                     ?.length > 0 ? (
//                                     <>
//                                         <Box
//                                             sx={{
//                                                 display:
//                                                     "flex",
//                                                 alignItems:
//                                                     "center",
//                                                 justifyContent:
//                                                     "space-between",
//                                                 gap: 1,
//                                                 p: 1,
//                                                 borderRadius:
//                                                     "14px",
//                                                 background:
//                                                     COLORS.lightCamel
//                                             }}
//                                         >
//                                             <IconButton
//                                                 onClick={() =>
//                                                     handlePrev(
//                                                         selectedChild
//                                                     )
//                                                 }
//                                                 disabled={
//                                                     currentComplaintIndex(
//                                                         selectedChild
//                                                     ) === 0
//                                                 }
//                                                 sx={{
//                                                     color:
//                                                         COLORS.black
//                                                 }}
//                                             >
//                                                 <ArrowBackIosNew fontSize="small" />
//                                             </IconButton>

//                                             <Typography
//                                                 sx={{
//                                                     color:
//                                                         COLORS.black,
//                                                     fontWeight:
//                                                         800,
//                                                     fontSize:
//                                                         "0.8rem",
//                                                     textAlign:
//                                                         "center"
//                                                 }}
//                                             >
//                                                 {currentComplaintIndex(
//                                                     selectedChild
//                                                 ) +
//                                                     1}{" "}
//                                                 of{" "}
//                                                 {
//                                                     selectedChild
//                                                         .complaints
//                                                         .length
//                                                 }
//                                             </Typography>

//                                             <IconButton
//                                                 onClick={() =>
//                                                     handleNext(
//                                                         selectedChild,
//                                                         selectedChild
//                                                             .complaints
//                                                             .length
//                                                     )
//                                                 }
//                                                 disabled={
//                                                     currentComplaintIndex(
//                                                         selectedChild
//                                                     ) ===
//                                                     selectedChild
//                                                         .complaints
//                                                         .length -
//                                                         1
//                                                 }
//                                                 sx={{
//                                                     color:
//                                                         COLORS.black
//                                                 }}
//                                             >
//                                                 <ArrowForward fontSize="small" />
//                                             </IconButton>
//                                         </Box>

//                                         <Box
//                                             sx={{
//                                                 maxHeight: 260,
//                                                 overflowY:
//                                                     "auto",
//                                                 display:
//                                                     "flex",
//                                                 flexDirection:
//                                                     "column",
//                                                 gap: 1.5
//                                             }}
//                                         >
//                                             {selectedChild.complaints.map(
//                                                 (
//                                                     complaint,
//                                                     index
//                                                 ) => {
//                                                     const isActive =
//                                                         index ===
//                                                         currentComplaintIndex(
//                                                             selectedChild
//                                                         );

//                                                     return (
//                                                         <Box
//                                                             key={
//                                                                 complaint.id
//                                                             }
//                                                             sx={{
//                                                                 p: {
//                                                                     xs: 1.5,
//                                                                     sm: 2
//                                                                 },
//                                                                 borderRadius:
//                                                                     "15px",
//                                                                 background:
//                                                                     isActive
//                                                                         ? "linear-gradient(135deg, #fff1df, #ffffff)"
//                                                                         : COLORS.white,
//                                                                 border: `1px solid ${
//                                                                     isActive
//                                                                         ? COLORS.orange
//                                                                         : COLORS.border
//                                                                 }`
//                                                             }}
//                                                         >
//                                                             <Typography
//                                                                 sx={{
//                                                                     color:
//                                                                         COLORS.black,
//                                                                     fontWeight:
//                                                                         900,
//                                                                     fontSize:
//                                                                         {
//                                                                             xs: "0.85rem",
//                                                                             sm: "0.95rem"
//                                                                         }
//                                                                 }}
//                                                             >
//                                                                 {
//                                                                     complaint.title
//                                                                 }
//                                                             </Typography>

//                                                             {isActive && (
//                                                                 <Typography
//                                                                     sx={{
//                                                                         mt: 1,
//                                                                         color:
//                                                                             "#4b5563",
//                                                                         fontSize:
//                                                                             {
//                                                                                 xs: "0.78rem",
//                                                                                 sm: "0.88rem"
//                                                                             },
//                                                                         lineHeight: 1.6,
//                                                                         whiteSpace:
//                                                                             "pre-wrap"
//                                                                     }}
//                                                                 >
//                                                                     {
//                                                                         complaint.description
//                                                                     }
//                                                                 </Typography>
//                                                             )}
//                                                         </Box>
//                                                     );
//                                                 }
//                                             )}
//                                         </Box>
//                                     </>
//                                 ) : (
//                                     <Typography
//                                         sx={{
//                                             color: COLORS.gray,
//                                             fontSize:
//                                                 "0.9rem"
//                                         }}
//                                     >
//                                         No complaints recorded.
//                                     </Typography>
//                                 )}
//                             </SectionCard>
//                         </Box>
//                     </Box>
// ) : showGrades ? (

//     <ParentsStudentGrades

//         selectedChild={selectedChild}

//         onBack={() =>
//             setShowGrades(false)
//         }

//     />

// ) : (

//     <TransActionPerStudent

//         selectedChild={selectedChild}

//         onBack={() =>
//             setShowPayments(false)
//         }

//     />

// )}
//             </>
//         )}
//     </Box>
// );


// };

// export default LinkStudents;


